import React, { useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { TextField, Box, InputAdornment, IconButton, Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import { ReactComponent as CalendarIcon } from '../../../assets/PepWise2.0/Calendar.svg';
import { ReactComponent as CalendarIconActive } from '../../../assets/PepWise2.0/Calendar-Active.svg';
import Calendar from '../Calender';

interface PepwiseTextInputProps {
  label?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  type?: 'text' | 'date';
  name?: string;
  showIcon?: boolean;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;
  selectedDate?: Dayjs | null;
  onDateChange?: (date: Dayjs | null) => void;
}

const PepwiseTextInput: React.FC<PepwiseTextInputProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  readOnly = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  type = 'text',
  name,
  showIcon = true,
  minDate = null,
  maxDate = null,
  selectedDate = null,
  onDateChange,
}) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const today = dayjs().endOf('day');

  const effectiveMaxDate = useMemo(() => {
    if (!maxDate) return today;
    return maxDate.isBefore(today, 'day') ? maxDate.endOf('day') : today;
  }, [maxDate, today]);

  const effectiveMinDate = useMemo(() => {
    return minDate ? minDate.startOf('day') : null;
  }, [minDate]);

  const initialMonth = useMemo(() => {
    if (selectedDate) return selectedDate;
    if (effectiveMaxDate) return effectiveMaxDate;
    return dayjs();
  }, [selectedDate, effectiveMaxDate]);

  const [month, setMonth] = useState(initialMonth);

  const errorLabel = error ? 'Error' : label;
  const isActive = isFocused || isHovered || Boolean(anchorEl);
  const isDateType = type === 'date';
  const shouldShowCalendarIcon = showIcon && isDateType;

  const clampDate = (date: Dayjs) => {
    let next = date;
    if (effectiveMinDate && next.isBefore(effectiveMinDate, 'day')) {
      next = effectiveMinDate;
    }
    if (effectiveMaxDate && next.isAfter(effectiveMaxDate, 'day')) {
      next = effectiveMaxDate;
    }
    return next;
  };

  const isDisabledDate = (date: Dayjs) => {
    if (effectiveMinDate && date.isBefore(effectiveMinDate, 'day')) return true;
    if (effectiveMaxDate && date.isAfter(effectiveMaxDate, 'day')) return true;
    return false;
  };

  const canGoPrevMonth = useMemo(() => {
    if (!effectiveMinDate) return true;
    const prevMonth = month.subtract(1, 'month');
    return !prevMonth.endOf('month').isBefore(effectiveMinDate, 'day');
  }, [month, effectiveMinDate]);

  const canGoNextMonth = useMemo(() => {
    const nextMonth = month.add(1, 'month');
    return !nextMonth.startOf('month').isAfter(effectiveMaxDate, 'day');
  }, [month, effectiveMaxDate]);

  const handleCalendarOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || readOnly || !isDateType) return;
    setAnchorEl(e.currentTarget);
    setMonth(clampDate(selectedDate || dayjs()));
  };

  const handleCalendarClose = () => {
    setAnchorEl(null);
  };

  const handleDateClick = (date: Dayjs) => {
    if (isDisabledDate(date) || !onDateChange) return;
    onDateChange(date);
    handleCalendarClose();
  };

  const handleClearDate = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onDateChange?.(null);
    handleCalendarClose();
  };

  const inRange = (date: Dayjs) => {
    return !!selectedDate && date.isSame(selectedDate, 'day');
  };

  const isStart = (date: Dayjs) => {
    return !!selectedDate && date.isSame(selectedDate, 'day');
  };

  const isEnd = (date: Dayjs) => {
    return !!selectedDate && date.isSame(selectedDate, 'day');
  };

  return (
    <>
      <Box
        sx={{ width: fullWidth ? '100%' : 'auto' }}
        onMouseEnter={() => !disabled && !readOnly && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <TextField
          fullWidth={fullWidth}
          label={errorLabel}
          value={value ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (!isDateType) {
              onChange?.(e);
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          helperText={helperText}
          type="text"
          name={name}
          variant="outlined"
          required={required}
          InputProps={{
            readOnly: readOnly || isDateType,
            notched: true,
            endAdornment: shouldShowCalendarIcon ? (
              <InputAdornment position="end">
                {isDateType && selectedDate && !disabled && !readOnly && (
                  <IconButton
                    edge="end"
                    onClick={handleClearDate}
                    sx={{
                      p: 0,
                      mr: 0.5,
                    }}
                  >
                    <CloseIcon
                      sx={{
                        fontSize: 20,
                        color: theme.palette.inputNew.inputIcon,
                      }}
                    />
                  </IconButton>
                )}

                <IconButton
                  onClick={handleCalendarOpen}
                  edge="end"
                  disableRipple
                  disabled={disabled || readOnly}
                >
                  {isActive ? (
                    <CalendarIconActive
                      style={{
                        width: '28px',
                        height: '28px',
                        transform: 'translate(0.75px, 0.75px)',
                        rotate: '0deg',
                        opacity: 1,
                      }}
                    />
                  ) : (
                    <CalendarIcon
                      style={{
                        width: '28px',
                        height: '28px',
                        transform: 'translate(0.75px, 0.75px)',
                        rotate: '0deg',
                        opacity: 1,
                      }}
                    />
                  )}
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            '& .MuiInputLabel-root': {
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              color: `${theme.palette.inputNew.labelText} !important`,
              opacity: '1 !important',
              zIndex: 10,
              transition: 'all 0.2s ease-in-out',
              transform: 'translate(14px, 14px) scale(1)',
              backgroundColor: 'transparent',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(14px, -12px) scale(1)',
                color: error
                  ? `${theme.palette.inputNew.error} !important`
                  : `${theme.palette.inputNew.labelText} !important`,
                fontSize: '14px',
                fontWeight: 300,
                fontStyle: 'italic',
                lineHeight: '1',
                letterSpacing: '0%',
                backgroundColor: disabled || readOnly ? '#EEEEEE !important' : 'white !important',
                padding: '0 8px',
                opacity: '1 !important',
                display: 'inline-block',
                width: 'auto',
                visibility: 'visible',
                zIndex: 10,
              },
            },

            '& .MuiOutlinedInput-root': {
              backgroundColor: disabled || readOnly ? '#EEEEEE' : 'transparent',
              borderRadius: '4px',
              minHeight: '48px',
              transition: 'all 0.2s ease-in-out',

              '& fieldset': {
                borderColor: `${theme.palette.inputNew.inputBorder} !important`,
                borderWidth: '0.8px',
              },

              '& .MuiOutlinedInput-notchedOutline': {
                borderWidth: '0.8px',
                '& legend': {
                  fontSize: '10px',
                  fontStyle: 'italic',
                  visibility: 'hidden',
                  width: 'auto',
                  maxWidth: '100%',
                },
              },

              '&:hover fieldset': {
                borderColor: error
                  ? theme.palette.inputNew.error
                  : `${theme.palette.inputNew.inputBorderHover} !important`,
                borderWidth: '1px',
              },

              '&.Mui-focused fieldset': {
                borderColor: error
                  ? theme.palette.inputNew.error
                  : `${theme.palette.inputNew.inputBorderFocus} !important`,
                borderWidth: '1.2px',
              },

              '&.Mui-disabled fieldset': {
                borderColor: '#D4D9E2 !important',
                borderWidth: '0.8px',
              },

              '&.Mui-error fieldset': {
                borderColor: `${theme.palette.inputNew.error} !important`,
                borderWidth: '1px',
              },

              '& svg': {
                color: error
                  ? `${theme.palette.inputNew.error} !important`
                  : disabled || readOnly
                  ? 'rgba(113, 117, 143, 0.4) !important'
                  : `${theme.palette.inputNew.inputIcon} !important`,
                fill: 'currentColor',
                opacity: '1 !important',
                transition: 'all 0.2s ease-in-out',
                '& path, & circle': {
                  fill: error
                    ? `${theme.palette.inputNew.error} !important`
                    : disabled || readOnly
                    ? 'rgba(113, 117, 143, 0.4) !important'
                    : 'currentColor',
                  transition: 'all 0.2s ease-in-out',
                },
              },

              '&:hover svg, &.Mui-focused svg': {
                color: error
                  ? `${theme.palette.inputNew.error} !important`
                  : disabled || readOnly
                  ? 'rgba(113, 117, 143, 0.4) !important'
                  : `${theme.palette.inputNew.inputBorderFocus} !important`,
                fill: 'currentColor',
                '& path, & circle': {
                  fill: error
                    ? `${theme.palette.inputNew.error} !important`
                    : disabled || readOnly
                    ? 'rgba(113, 117, 143, 0.4) !important'
                    : 'currentColor',
                },
              },

              '&.Mui-error svg': {
                color: `${theme.palette.inputNew.error} !important`,
                fill: `${theme.palette.inputNew.error} !important`,
                '& path, & circle': {
                  fill: `${theme.palette.inputNew.error} !important`,
                },
              },
            },

            '& .MuiInputBase-input': {
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '25px',
              letterSpacing: '0%',
              color: error
                ? `${theme.palette.inputNew.error} !important`
                : `${theme.palette.inputNew.inputText} !important`,
              fontFamily: 'Montserrat, sans-serif',
              opacity: '1 !important',
              WebkitTextFillColor: error
                ? `${theme.palette.inputNew.error} !important`
                : `${theme.palette.inputNew.inputText} !important`,
              '&.Mui-disabled': {
                WebkitTextFillColor: '#71758F !important',
                color: '#71758F !important',
              },
              '&::placeholder': {
                color: '#A9ADCB',
                opacity: 0.8,
                fontStyle: 'italic',
              },
            },

            '& .MuiFormHelperText-root': {
              marginLeft: 0,
              marginTop: '4px',
              fontSize: '11px',
              fontFamily: 'Montserrat, sans-serif',
              color: error ? theme.palette.error.main : '#71758F',
            },
          }}
        />
      </Box>

      {isDateType && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCalendarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                backgroundColor: `${theme.palette.dateFilter.popoverBackground} !important`,
                backgroundImage: 'none !important',
                borderRadius: '8px',
                boxShadow: theme.palette.dateFilter.popoverShadow,
                overflow: 'hidden',
              },
            },
          }}
        >
          <Calendar
            month={month}
            setMonth={setMonth}
            startDate={selectedDate}
            endDate={selectedDate}
            hoverDate={null}
            setHoverDate={() => {}}
            onDateClick={handleDateClick}
            isDisabledDate={isDisabledDate}
            inRange={inRange}
            isStart={isStart}
            isEnd={isEnd}
            canGoPrevMonth={canGoPrevMonth}
            canGoNextMonth={canGoNextMonth}
          />
        </Popover>
      )}
    </>
  );
};

export default PepwiseTextInput;
